import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  GROUP_TYPES,
  GROUP_VISIBILITY,
  GROUP_NAME_MAX_LENGTH,
  GROUP_DESCRIPTION_MAX_LENGTH,
  COLORS,
  SKILL_LEVELS
} from "../constants";

export default function CreateGroupModal({
  show,
  onClose,
  onSubmit,
  groupForm,
  onChange,
  optionTile
}) {
  const [errors, setErrors] = useState({});

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    // Reset errors
    setErrors({});
    
    // Validate form before submitting
    const newErrors = {};
    if (!groupForm.name.trim()) {
      newErrors.name = 'Group name is required';
    }
    if (!groupForm.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!GROUP_VISIBILITY[groupForm.visibility.toUpperCase()]) {
      newErrors.visibility = 'Visibility is required';
    }
    if (!GROUP_TYPES[groupForm.gender.toUpperCase()]) {
      newErrors.gender = 'Group type is required';
    }
    if (!groupForm.skillLevel) {
      newErrors.skillLevel = 'Skill level is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(e);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full max-h-[90vh] sm:max-h-[85vh] rounded-lg max-w-md shadow-lg flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white px-4 py-3 sm:py-4 border-b">
          <h3 className="text-xl font-bold text-center text-textDark">Create New Group</h3>
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6 space-y-6 flex-1 overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold mb-2 text-textDark" htmlFor="name">
              Group Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="name"
              id="name"
              value={groupForm.name}
              onChange={(e) => onChange("name", e.target.value)}
              maxLength={GROUP_NAME_MAX_LENGTH}
              className={`w-full border rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-accent ${
                errors.name ? 'border-red-500' : 'border-border'
              }`}
              required
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-textDark" htmlFor="description">
              Description <span className="text-red-600">*</span>
            </label>
            <textarea
              name="description"
              id="description"
              value={groupForm.description}
              onChange={(e) => onChange("description", e.target.value)}
              maxLength={GROUP_DESCRIPTION_MAX_LENGTH}
              rows={4}
              className={`w-full border rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-accent ${
                errors.description ? 'border-red-500' : 'border-border'
              }`}
              required
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Visibility</label>
            <div className="grid grid-cols-2 gap-2">
              {optionTile("visibility", GROUP_VISIBILITY.PUBLIC, groupForm.visibility, "Public")}
              {optionTile("visibility", GROUP_VISIBILITY.PRIVATE, groupForm.visibility, "Private")}
            </div>
            {errors.visibility && <p className="mt-1 text-sm text-red-600">{errors.visibility}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Group Type</label>
            <div className="grid grid-cols-3 gap-2">
              {optionTile("gender", GROUP_TYPES.MALE, groupForm.gender, "Male")}
              {optionTile("gender", GROUP_TYPES.FEMALE, groupForm.gender, "Female")}
              {optionTile("gender", GROUP_TYPES.MIXED, groupForm.gender, "Mixed")}
            </div>
            {errors.gender && <p className="mt-1 text-sm text-red-600">{errors.gender}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Skill Level <span className="text-red-600">*</span></label>
            <div className="grid grid-cols-4 gap-2">
              {Object.values(SKILL_LEVELS).map((level) => (
                optionTile("skillLevel", level, groupForm.skillLevel, level)
              ))}
            </div>
            {errors.skillLevel && <p className="mt-1 text-sm text-red-600">{errors.skillLevel}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-4 py-3 sm:px-6 border-t">
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-primary text-primary hover:bg-primary/5 rounded-lg text-base font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-primary text-white hover:bg-primaryDark rounded-lg text-base font-medium"
            >
              Create
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

CreateGroupModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  groupForm: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    visibility: PropTypes.oneOf(Object.values(GROUP_VISIBILITY)).isRequired,
    gender: PropTypes.oneOf(Object.values(GROUP_TYPES)).isRequired,
    skillLevel: PropTypes.oneOf(Object.values(SKILL_LEVELS)).isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  optionTile: PropTypes.func.isRequired
}; 